import React, { useState, useEffect, useRef } from 'react';
import './image_viewer.css';

const ImageViewer = ({ imageUrl, isOpen, onClose }) => {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  
  // Touch-specific states
  const [isTouching, setIsTouching] = useState(false);
  const [lastTouchDistance, setLastTouchDistance] = useState(0);
  const [initialScale, setInitialScale] = useState(1);
  const [lastTapTime, setLastTapTime] = useState(0);
  const [touchStartPosition, setTouchStartPosition] = useState({ x: 0, y: 0 });
  
  const imageRef = useRef(null);
  const containerRef = useRef(null);

  //   console.log('ImageViewer - isOpen:', isOpen, 'imageUrl:', imageUrl);

  useEffect(() => {
    if (isOpen) {
      // Reset zoom and position when opening
      setScale(1);
      setPosition({ x: 0, y: 0 });
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    // Handle keyboard events
    const handleKeyDown = (e) => {
      if (!isOpen) return;

      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === '+' || e.key === '=') {
        handleZoomIn();
      } else if (e.key === '-') {
        handleZoomOut();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  const handleZoomIn = () => {
    setScale(prevScale => Math.min(prevScale * 1.2, 5));
  };

  const handleZoomOut = () => {
    setScale(prevScale => Math.max(prevScale / 1.2, 0.5));
  };

  const handleWheel = (e) => {
    e.preventDefault();
    if (e.deltaY < 0) {
      handleZoomIn();
    } else {
      handleZoomOut();
    }
  };

  const handleMouseDown = (e) => {
    if (scale > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && scale > 1) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleDoubleClick = () => {
    if (scale === 1) {
      setScale(2);
    } else {
      setScale(1);
      setPosition({ x: 0, y: 0 });
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Touch helper functions
  const getTouchDistance = (touches) => {
    if (touches.length < 2) return 0;
    const touch1 = touches[0];
    const touch2 = touches[1];
    return Math.sqrt(
      Math.pow(touch2.clientX - touch1.clientX, 2) + 
      Math.pow(touch2.clientY - touch1.clientY, 2)
    );
  };

  const getTouchCenter = (touches) => {
    if (touches.length === 1) {
      return { x: touches[0].clientX, y: touches[0].clientY };
    } else if (touches.length >= 2) {
      return {
        x: (touches[0].clientX + touches[1].clientX) / 2,
        y: (touches[0].clientY + touches[1].clientY) / 2
      };
    }
    return { x: 0, y: 0 };
  };

  // Touch event handlers
  const handleTouchStart = (e) => {
    e.preventDefault();
    const touches = e.touches;
    setIsTouching(true);

    if (touches.length === 1) {
      // Single touch - prepare for dragging or double tap
      const touch = touches[0];
      setTouchStartPosition({ x: touch.clientX, y: touch.clientY });
      
      if (scale > 1) {
        setIsDragging(true);
        setDragStart({
          x: touch.clientX - position.x,
          y: touch.clientY - position.y
        });
      }

      // Handle double tap
      const currentTime = Date.now();
      if (currentTime - lastTapTime < 300) {
        handleDoubleClick();
        // Prevent any further processing for double tap
        setLastTapTime(0);
        return;
      }
      setLastTapTime(currentTime);

    } else if (touches.length === 2) {
      // Two finger touch - prepare for pinch zoom
      setIsDragging(false);
      const distance = getTouchDistance(touches);
      setLastTouchDistance(distance);
      setInitialScale(scale);
    }
  };

  const handleTouchMove = (e) => {
    e.preventDefault();
    const touches = e.touches;

    if (touches.length === 1 && isDragging && scale > 1) {
      // Single finger drag
      const touch = touches[0];
      const newPosition = {
        x: touch.clientX - dragStart.x,
        y: touch.clientY - dragStart.y
      };
      
      // Optional: Add boundary constraints to prevent dragging too far
      setPosition(newPosition);
      
    } else if (touches.length === 2) {
      // Two finger pinch zoom
      const distance = getTouchDistance(touches);
      if (lastTouchDistance > 0) {
        const scaleChange = distance / lastTouchDistance;
        const newScale = Math.min(Math.max(initialScale * scaleChange, 0.5), 5);
        setScale(newScale);
        
        // Reset position if zooming out to 1x
        if (newScale <= 1) {
          setPosition({ x: 0, y: 0 });
        }
      }
    }
  };

  const handleTouchEnd = (e) => {
    e.preventDefault();
    setIsTouching(false);
    setIsDragging(false);
    setLastTouchDistance(0);

    // Only handle tap-to-close if it was a single touch and no dragging occurred
    if (e.touches.length === 0 && e.changedTouches.length === 1) {
      const touch = e.changedTouches[0];
      const touchEndPosition = { x: touch.clientX, y: touch.clientY };
      const distance = Math.sqrt(
        Math.pow(touchEndPosition.x - touchStartPosition.x, 2) + 
        Math.pow(touchEndPosition.y - touchStartPosition.y, 2)
      );
      
      // If it was a very small movement (tap), and we're not zoomed, and tap was on backdrop
      if (distance < 10 && scale <= 1 && e.target === e.currentTarget) {
        onClose();
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="image-viewer-backdrop"
      onClick={handleBackdropClick}
      ref={containerRef}
    >
      <div className="image-viewer-container">
        {/* Header with controls */}
        <div className="image-viewer-header">
          <div className="image-viewer-controls">
            <button
              className="image-viewer-btn"
              onClick={handleZoomOut}
              title="Alejar (-)"
            >
              <i className="fas fa-search-minus"></i>
            </button>
            <span className="image-viewer-zoom-level">
              {Math.round(scale * 100)}%
            </span>
            <button
              className="image-viewer-btn"
              onClick={handleZoomIn}
              title="Acercar (+)"
            >
              <i className="fas fa-search-plus"></i>
            </button>
            <button
              className="image-viewer-btn"
              onClick={() => {
                setScale(1);
                setPosition({ x: 0, y: 0 });
              }}
              title="Tamaño original"
            >
              <i className="fas fa-expand-arrows-alt"></i>
            </button>
          </div>
          <button
            className="image-viewer-close"
            onClick={onClose}
            title="Cerrar (Esc)"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>

        {/* Image container */}
        <div
          className="image-viewer-content"
          onWheel={handleWheel}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onDoubleClick={handleDoubleClick}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          style={{ 
            cursor: scale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'zoom-in',
            touchAction: 'none' // Prevent default touch behaviors
          }}
        >
          {imageUrl && (
            <img
              ref={imageRef}
              src={imageUrl}
              alt="Vista ampliada"
              className="image-viewer-image"
              style={{
                transform: `scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)`,
                transformOrigin: 'center center'
              }}
              draggable={false}
            //   onLoad={() => console.log('Image loaded:', imageUrl)}
            //   onError={(e) => console.error('Image load error:', e, imageUrl)}
            />
          )}
        </div>

        {/* Instructions */}
        <div className="image-viewer-instructions">
          <small className="text-muted">
            Doble clic/toque para zoom • Pellizcar para acercar/alejar • Arrastrar para mover • ESC para cerrar
          </small>
        </div>
      </div>
    </div>
  );
};

export default ImageViewer;
